import React, { useEffect, useRef, useState } from "react";
import { Outlet, Link, NavLink } from "react-router-dom";
import { SignedIn, SignedOut, SignInButton, SignUpButton } from "@clerk/clerk-react";
import { Menu, X, ChevronDown } from "lucide-react";
import Footer from "../Footer";
import DarkModeToggle from "../DarkModeToggle";
import Button from "../ui/Button";

const FOR_LINKS = [
  { to: "/for/recruiters",   label: "Recruiters",   blurb: "Career fairs, open roles, candidate intake." },
  { to: "/for/sales",        label: "Sales",        blurb: "Demos, decks, lead capture." },
  { to: "/for/real-estate",  label: "Real estate",  blurb: "Listings, tours, sign-in QR." },
  { to: "/for/consultants",  label: "Consultants",  blurb: "Engagement decks, intake, calendar." },
  { to: "/for/founders",     label: "Founders",     blurb: "Press kits, decks, calendar in one link." },
];

const TEAMS_LINK = {
  to: "/teams",
  label: "Teams",
  blurb: "Provisioning cards for everyone — sales orgs, recruiting ops, founding teams. Admin dashboard, brand-locked themes, bulk card ordering.",
  price: "$3.99 / seat / month",
};

const COMPARE_LINKS = [
  { to: "/compare/whobrew-vs-popl",  label: "vs Popl" },
  { to: "/compare/whobrew-vs-blinq", label: "vs Blinq" },
  { to: "/compare/whobrew-vs-linq",  label: "vs Linq" },
  { to: "/compare/whobrew-vs-v1ce",  label: "vs V1CE" },
];

const PublicLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<"for" | "compare" | null>(null);
  const navRef = useRef<HTMLDivElement | null>(null);

  // Close mega menu on outside click + Escape
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!navRef.current) return;
      if (!navRef.current.contains(e.target as Node)) setOpenMenu(null);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpenMenu(null);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const closeMobile = () => setMobileOpen(false);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 text-sm font-medium transition-colors ${
      isActive
        ? "text-coffee-900 dark:text-cream-50"
        : "text-coffee-700 dark:text-cream-200 hover:text-coffee-900 dark:hover:text-cream-50"
    }`;

  return (
    <>
      <nav
        ref={navRef}
        className="sticky top-0 z-40 bg-cream-50/85 dark:bg-coffee-900/85 backdrop-blur-sm border-b border-latte-300 dark:border-latte-800"
      >
        <div className="max-w-container mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3 group">
              <span className="font-display text-xl text-coffee-900 dark:text-cream-50 tracking-tight">
                WhoBrew
              </span>
              <span className="hidden sm:inline font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200">
                est. 2024
              </span>
            </Link>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-1">
              <NavLink to="/how-it-works" className={linkClass}>How it works</NavLink>

              {/* For mega-menu */}
              <button
                onClick={() => setOpenMenu(openMenu === "for" ? null : "for")}
                className="px-3 py-2 text-sm font-medium text-coffee-700 dark:text-cream-200 hover:text-coffee-900 dark:hover:text-cream-50 inline-flex items-center gap-1"
                aria-expanded={openMenu === "for"}
              >
                For <ChevronDown className="w-3.5 h-3.5" />
              </button>

              {/* Compare mega-menu */}
              <button
                onClick={() => setOpenMenu(openMenu === "compare" ? null : "compare")}
                className="px-3 py-2 text-sm font-medium text-coffee-700 dark:text-cream-200 hover:text-coffee-900 dark:hover:text-cream-50 inline-flex items-center gap-1"
                aria-expanded={openMenu === "compare"}
              >
                Compare <ChevronDown className="w-3.5 h-3.5" />
              </button>

              <NavLink to="/pricing" className={linkClass}>Pricing</NavLink>
              <NavLink to="/trust" className={linkClass}>Trust</NavLink>
            </div>

            {/* Auth + theme */}
            <div className="hidden lg:flex items-center gap-3">
              <DarkModeToggle />
              <SignedOut>
                <SignInButton mode="modal">
                  <Button size="sm" variant="ghost">Sign in</Button>
                </SignInButton>
                <SignUpButton mode="modal">
                  <Button size="sm">Claim handle</Button>
                </SignUpButton>
              </SignedOut>
              <SignedIn>
                <Button to="/my-card" size="sm">Dashboard</Button>
              </SignedIn>
            </div>

            {/* Mobile button */}
            <div className="lg:hidden flex items-center gap-2">
              <DarkModeToggle />
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="text-coffee-900 dark:text-cream-50 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Toggle menu"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mega menu — For. Five individual personas above; Teams gets the
              dark spotlight strip at the bottom (Option C from file 07). */}
          {openMenu === "for" && (
            <div className="hidden lg:block absolute left-0 right-0 top-full bg-cream-50 dark:bg-coffee-900 border-b border-latte-300 dark:border-latte-800 shadow-sm">
              <div className="max-w-container mx-auto px-6 sm:px-8 lg:px-12 pt-8">
                <div className="grid grid-cols-2 gap-x-12 gap-y-4 pb-8">
                  {FOR_LINKS.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setOpenMenu(null)}
                      className="group flex flex-col gap-1 py-2 hover:opacity-70 transition-opacity"
                    >
                      <span className="font-display text-headline text-coffee-900 dark:text-cream-50">
                        {l.label}<span className="text-accent-500">.</span>
                      </span>
                      <span className="text-body-sm text-coffee-700 dark:text-cream-200">{l.blurb}</span>
                    </Link>
                  ))}
                </div>
              </div>
              {/* Teams spotlight — full-width dark strip */}
              <Link
                to={TEAMS_LINK.to}
                onClick={() => setOpenMenu(null)}
                className="block bg-coffee-900 text-cream-50 hover:bg-coffee-800 transition-colors"
              >
                <div className="max-w-container mx-auto px-6 sm:px-8 lg:px-12 py-6 flex items-center justify-between gap-6">
                  <div>
                    <p className="font-mono text-eyebrow uppercase text-cream-200 mb-1">Teams</p>
                    <p className="font-display text-headline">
                      Provisioning cards for everyone? <span className="italic font-display-italic">Read this</span><span className="text-accent-500">.</span>
                    </p>
                    <p className="mt-1 text-body-sm text-cream-200/80">{TEAMS_LINK.blurb}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="font-mono text-eyebrow uppercase text-accent-400 hidden sm:inline">{TEAMS_LINK.price}</span>
                    <ChevronDown className="w-4 h-4 -rotate-90" />
                  </div>
                </div>
              </Link>
            </div>
          )}

          {/* Mega menu — Compare */}
          {openMenu === "compare" && (
            <div className="hidden lg:block absolute left-0 right-0 top-full bg-cream-50 dark:bg-coffee-900 border-b border-latte-300 dark:border-latte-800 shadow-sm">
              <div className="max-w-container mx-auto px-6 sm:px-8 lg:px-12 py-8 grid grid-cols-4 gap-6">
                {COMPARE_LINKS.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpenMenu(null)}
                    className="font-display text-headline text-coffee-900 dark:text-cream-50 hover:opacity-70 transition-opacity"
                  >
                    {l.label}<span className="text-accent-500">.</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Mobile menu */}
          {mobileOpen && (
            <div className="lg:hidden border-t border-latte-300 dark:border-latte-800 py-4 space-y-1">
              {[
                { to: "/how-it-works", label: "How it works" },
                { to: "/pricing",      label: "Pricing" },
                { to: "/trust",        label: "Trust" },
                { to: "/contact",      label: "Contact" },
              ].map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={closeMobile}
                  className="block px-3 py-3 text-base text-coffee-900 dark:text-cream-50"
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="pt-3 mt-3 border-t border-latte-300 dark:border-latte-800">
                <p className="px-3 mb-2 font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200">For</p>
                {FOR_LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={closeMobile}
                    className="block px-3 py-2 text-sm text-coffee-700 dark:text-cream-200"
                  >
                    {l.label}
                  </NavLink>
                ))}
                {/* Teams spotlight — dark strip at the bottom of the For section */}
                <Link
                  to={TEAMS_LINK.to}
                  onClick={closeMobile}
                  className="block mt-2 -mx-0 bg-coffee-900 text-cream-50 px-3 py-3 hover:bg-coffee-800 transition-colors"
                >
                  <p className="font-display text-headline">{TEAMS_LINK.label}<span className="text-accent-500">.</span></p>
                  <p className="text-xs text-cream-200/80 mt-0.5">For admins · {TEAMS_LINK.price}</p>
                </Link>
              </div>
              <div className="pt-3 mt-3 border-t border-latte-300 dark:border-latte-800 flex gap-3 px-3">
                <SignedOut>
                  <SignInButton mode="modal">
                    <Button size="sm" variant="ghost" fullWidth>Sign in</Button>
                  </SignInButton>
                  <SignUpButton mode="modal">
                    <Button size="sm" fullWidth>Claim handle</Button>
                  </SignUpButton>
                </SignedOut>
                <SignedIn>
                  <Button to="/my-card" size="sm" fullWidth onClick={closeMobile}>Dashboard</Button>
                </SignedIn>
              </div>
            </div>
          )}
        </div>
      </nav>

      <main id="main-content">
        <Outlet />
      </main>

      <Footer />
    </>
  );
};

export default PublicLayout;
